import { create } from 'zustand';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../services/api';

export const useChatStore = create((set, get) => ({
  socket: null,
  onlineUsers: {}, // Map of userId -> status/lastSeen
  activeConversation: null,
  conversations: [],
  messages: [],
  typingStatus: {}, // Map of userId -> boolean

  initSocket: (userId) => {
    const currentSocket = get().socket;
    if (currentSocket) return;

    const socket = io(API_BASE_URL, {
      withCredentials: true,
    });

    socket.on('connect', () => {
      socket.emit('join', userId);
    });

    socket.on('user_status', ({ userId: uId, status, lastSeen }) => {
      set((state) => ({
        onlineUsers: {
          ...state.onlineUsers,
          [uId]: { status, lastSeen }
        }
      }));
    });

    socket.on('receive_message', (message) => {
      const { activeConversation } = get();
      
      // If message belongs to active conversation, append it
      if (activeConversation && (
        String(message.sender_id?._id || message.sender_id) === String(activeConversation.otherUserId) ||
        String(message.receiver_id?._id || message.receiver_id) === String(activeConversation.otherUserId)
      )) {
        set((state) => ({ messages: [...state.messages, message] }));
        
        // Mark as seen if we are actively viewing this conversation
        socket.emit('message_seen', {
          messageId: message._id,
          senderId: message.sender_id._id,
          receiverId: userId,
          conversationId: message.conversation_id
        });
      }

      // Update conversations list latest message and unread count
      set((state) => {
        const newConvs = [...state.conversations];
        const convIndex = newConvs.findIndex(c => 
          c.participants.some(p => p._id === message.sender_id._id || p._id === message.receiver_id._id)
        );

        if (convIndex !== -1) {
          const conv = newConvs[convIndex];
          conv.last_message = message;
          if (!activeConversation || activeConversation.otherUserId !== message.sender_id._id) {
             conv.unreadCount = (conv.unreadCount || 0) + 1;
          }
          // Move to top
          newConvs.splice(convIndex, 1);
          newConvs.unshift(conv);
        }
        return { conversations: newConvs };
      });
    });

    socket.on('message_sent', (message) => {
        const { activeConversation } = get();
        
        if (!activeConversation) return;

        const msgReceiverId = message.receiver_id?._id || message.receiver_id;
        
        // If it's the active conversation or the first message to this user
        if (String(message.conversation_id) === String(activeConversation.conversationId) || 
            String(msgReceiverId) === String(activeConversation.otherUserId)) {
            
            // If it's a new conversation, update the store with the newly generated conversationId
            if (!activeConversation.conversationId) {
                set({ activeConversation: { ...activeConversation, conversationId: message.conversation_id } });
                
                // Add to sidebar conversations list
                set((state) => ({
                    conversations: [{
                        _id: message.conversation_id,
                        participants: [message.sender_id, message.receiver_id],
                        last_message: message,
                        unreadCount: 0
                    }, ...state.conversations]
                }));
            } else {
                // Update existing conversation's last message
                set((state) => {
                    const newConvs = [...state.conversations];
                    const convIndex = newConvs.findIndex(c => c._id === message.conversation_id);
                    if (convIndex !== -1) {
                        const conv = newConvs[convIndex];
                        conv.last_message = message;
                        newConvs.splice(convIndex, 1);
                        newConvs.unshift(conv);
                    }
                    return { conversations: newConvs };
                });
            }

            set((state) => ({
                messages: [...state.messages, message]
            }));
        }
    });

    socket.on('messages_seen', ({ conversationId }) => {
      set((state) => ({
        messages: state.messages.map(m => 
          m.conversation_id === conversationId && m.status !== 'seen' 
            ? { ...m, status: 'seen' } 
            : m
        )
      }));
    });

    socket.on('typing', ({ senderId }) => {
      set((state) => ({
        typingStatus: { ...state.typingStatus, [senderId]: true }
      }));
    });

    socket.on('stop_typing', ({ senderId }) => {
      set((state) => ({
        typingStatus: { ...state.typingStatus, [senderId]: false }
      }));
    });

    set({ socket });
  },

  disconnectSocket: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null });
    }
  },

  setActiveConversation: (conversationData) => set({ activeConversation: conversationData }),
  setConversations: (conversations) => set({ conversations }),
  setMessages: (messages) => set({ messages }),
  
  sendMessage: (senderId, receiverId, content) => {
    const { socket, activeConversation } = get();
    if (socket) {
      socket.emit('send_message', {
        senderId,
        receiverId,
        content,
        conversationId: activeConversation?.conversationId
      });
    }
  },
  
  sendTyping: (senderId, receiverId) => {
    const { socket } = get();
    if (socket) socket.emit('typing', { senderId, receiverId });
  },
  
  sendStopTyping: (senderId, receiverId) => {
    const { socket } = get();
    if (socket) socket.emit('stop_typing', { senderId, receiverId });
  }
}));
